import * as React from 'react';
import { Box, ButtonBase } from '@material-ui/core';
import { CustomButton, CustomDialog, CustomIcon, CustomInputText } from '../../custom-forms';
import { useStyles, cx } from '../../custom-forms';
import { useTranslate } from '../../hooks/useTranslate';
import { CaseAttachment } from '../../interfaces/Case';

export interface ChangeOrderStatusDialogProps {
  open: boolean;
  comment: string;
  required: boolean;
  executing: boolean;
  hasOrderPartsChangedBySupplier: boolean;
  attachments: CaseAttachment[];
  onCommentChange: (value: string) => void;
  onUploadAttachment: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteAttachment: (id: number, fileName: string) => void;
  onDownloadAttachment: (fileName: string, originalFileName: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const ChangeOrderStatusDialog: React.FC<ChangeOrderStatusDialogProps> = ({
  open,
  comment,
  required,
  executing,
  hasOrderPartsChangedBySupplier,
  attachments,
  onCommentChange,
  onUploadAttachment,
  onDeleteAttachment,
  onDownloadAttachment,
  onClose,
  onSubmit
}) => {
  const c = useStyles();
  const t = useTranslate();

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      hideActions
      title={t('ORDER_DETAILS_DIALOG_TITLE', 'Modificare stare comanda')}
      cancelLabel={t('VEHICLE_SEARCH_FORM_VEHICLE_NO', 'Nu')}
      confirmLabel={t('CASE_COMMENT_SEND', 'Trimite')}
    >
      {hasOrderPartsChangedBySupplier ? (
        <Box className={c.mb10} style={{ color: 'red' }}>
          {t(
            'ORDER_NOTIFICATIONS_NO_CHANGES_ON_PARTS_SUPPLIER_LABEL',
            'Nu au fost efectuate modificări în comandă. La salvare, comanda va fi lansată automat. Doriți să continuați?'
          )}
        </Box>
      ) : null}

      <CustomInputText
        fullWidth
        id="textComment"
        name="textComment"
        multiline
        rows={5}
        disabled={executing}
        label={t('ORDER_PART_DETAILS_DIALOG_COMMENT_LABEL', 'Comentariu')}
        value={comment}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCommentChange(e.target.value)}
        validators={required ? ['required'] : undefined}
        errorMessages={required ? [t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')] : undefined}
      />

      {attachments.length > 0 && (
        <Box mt={1}>
          {attachments.map((attach, index) => (
            <Box key={index} display="flex" alignItems="center" width="100%">
              <Box flexGrow={1}>
                <CustomButton
                  variant="text"
                  color="primary"
                  style={{ textAlign: 'left', paddingLeft: 0 }}
                  onClick={() => onDownloadAttachment(attach.fileName, attach.originalFileName)}
                >
                  {attach.originalFileName}
                </CustomButton>
              </Box>
              <ButtonBase
                className={cx(c.iconBtn, c.iconBtnSm, c.iconBtnDanger)}
                disabled={executing}
                onClick={() => onDeleteAttachment(attach.id, attach.fileName)}
              >
                <CustomIcon name="trash" />
              </ButtonBase>
            </Box>
          ))}
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <input
          className="d-none"
          id="order-change-status-attach"
          onChange={onUploadAttachment}
          type="file"
          disabled={executing}
        />
        <label htmlFor="order-change-status-attach" style={{ marginBottom: 0 }}>
          <CustomButton variant="outlined" color="primary" component="div" disabled={executing}>
            {t('ORDER_PART_DETAILS_DIALOG_ATTACH_BTN_LABEL', 'Atasament')}
          </CustomButton>
        </label>
      </Box>

      <Box display="flex" justifyContent="flex-end" mt={2} style={{ gap: 8 }}>
        {hasOrderPartsChangedBySupplier ? (
          <CustomButton variant="outlined" disabled={executing} onClick={onClose}>
            {t('VEHICLE_SEARCH_FORM_VEHICLE_NO', 'Nu')}
          </CustomButton>
        ) : null}
        <CustomButton type="submit" disabled={executing} isLoading={executing}>
          {hasOrderPartsChangedBySupplier
            ? t('VEHICLE_SEARCH_FORM_VEHICLE_YES', 'Da')
            : t('CASE_COMMENT_SEND', 'Trimite')}
        </CustomButton>
      </Box>
    </CustomDialog>
  );
};

export default ChangeOrderStatusDialog;
